import {
  ApartmentOutlined,
  ArrowDownOutlined,
  ArrowRightOutlined,
  BarcodeOutlined,
  CalendarFilled,
  CalendarOutlined,
  CheckCircleFilled,
  DotChartOutlined,
  ExclamationCircleFilled,
  EyeFilled,
  EyeInvisibleFilled,
  ScissorOutlined,
  SearchOutlined
} from "@ant-design/icons";
import { Button, Form, Input, message, Table } from "antd";
import { useForm } from "antd/es/form/Form";
import { ColumnsType } from "antd/es/table";
import { DateTime } from "luxon";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import { changeShowLoading } from "../../components/Spinner/Spinner";
import styles from "../../index.style";
import { patientInfoState } from "../../recoil/atoms/patientInfoAtom";
import { sharedInternalInfoState } from "../../recoil/atoms/sharedInternalAtom";
import CookieService from "../../services/cookie.service";
import SurgeryService from "../../services/surgery.service";
import { AbnormalFilter } from "../../types/common.types";
import { DataType, SurgeryManagment } from "../../types/surgeries.types";
import { customTableTitle } from "../../utils/tables.utils";
import "./Surgeries.css";

function Surgeries() {
  let navigate = useNavigate();
  const location = useLocation();
  // eslint-disable-next-line
  const [_, setPatientInfo] = useRecoilState(patientInfoState);
  // eslint-disable-next-line
  const [__, setSharedInfo] = useRecoilState(sharedInternalInfoState);
  const yScroll = true; // disable yscroll
  const { t, i18n } = useTranslation();
  const [currentDate, setCurrentDate] = React.useState([
    location?.state?.date ||
      DateTime.now().startOf("day").toFormat("yyyy-MM-dd"),
    location?.state?.date || DateTime.now().endOf("day").toFormat("yyyy-MM-dd"),
  ] as string[]);
  const [dateToBeShown, setDateToBeShown] = useState("");
  const payload = useRef([] as Array<AbnormalFilter>);
  const [surgeryDetail, setSurgeryDetail] = useState(
    {} as DataType & { showing: boolean }
  );

  const scroll: { x?: number | string; y?: number | string } = {};
  if (yScroll) {
    scroll.y = 300;
  }

  function navigateToChecklist(record: any) {
    changeShowLoading(
      SurgeryService.getChecklistExist(record.uuid).then((check) => {

        const alternativeSurgeryBaselistUUID = record?.revisionDetail?.length
        ? record.revisionDetail[0].surgeryBaselist
        : "";
       
        // se spari un barcode, prendi i dati dopo || (or)
        const data = {
          firstName: record?.firstName || record.patient.first_name,
          lastName: record?.lastName || record.patient.last_name,
          tin: record?.tin || record.patient.tin,
          birthDay: record?.birthDay || record.patient.date_of_birth,
          patientUuid: record.patientUuid || record.patient.uuid,
          surgeryBaselistUUID: record.surgeryBaselistUUID || alternativeSurgeryBaselistUUID,
          status: record.status || record?.revision?.currentStatus || "",
        }

        setPatientInfo({
          firstName: data.firstName,
          lastName: data.lastName,
          tin: data.tin,
          birthDay: data.birthDay,
          surgeryDate:
            location?.state?.date ||
            DateTime.now().startOf("day").toFormat("yyyy-MM-dd"),
          uuid: data.patientUuid,
        });
        setSharedInfo({
          surgery: record.uuid,
          surgeryBaselistUUID: data.surgeryBaselistUUID,
          checklists: check,
          status: data.status
        });
        localStorage.setItem(
          "current.surgery",
          JSON.stringify({ surgery: record.uuid })
        );
        localStorage.setItem(
          "current.checklists",
          JSON.stringify({ checklists: check })
        );
        localStorage.setItem(
          "current.status",
          JSON.stringify({ status: data.status })
        );
        localStorage.setItem(
          'patientData',
          JSON.stringify({patientData: data})
        )
        navigate("/checklist/PO", {
          state: { uuid: record.uuid, type: "path-tab" },
        });
      })
    );
  }

  const headerRender = (filter: Array<string> = []): ColumnsType<DataType> => {
    const cols: any = [
      {
        title: customTableTitle(`${t("START")}`, ""),
        dataIndex: "slotStart",
        key: "slotStart",
        width: "100px",
        render: (date: string) =>
          date ? (
            <div className="font-bold text-teal-800">
              {DateTime.fromISO(date, {
                zone: "Europe/Rome",
              }).toLocaleString(DateTime.TIME_24_SIMPLE)}
            </div>
          ) : null,
      },
      {
        title: customTableTitle(`${t("PATIENT")}`, ""),
        dataIndex: "name",
        key: "name",
        width: "20%",
        render: (text: string, record: any) => (
          <div>
            {record.firstName} {record.lastName}
          </div>
        ),
      },
      {
        title: customTableTitle(`${t("SURGERY")}`, ""),
        dataIndex: "surgery",
        key: "surgery",
        render: (text: string, record: any) => (
          <div>
            <div>{record.surgery.description}</div>
            <div className="text-xs text-teal-600">{record.surgery.code}</div>
          </div>
        ),
      },
      {
        title: customTableTitle(`${t("DEPARTMENT")}`, ""),
        dataIndex: "guardDept",
        key: "guardDept",
        width: "20%",
        render: (text: string, record: any) => (
          <div>{record.wardDept.description}</div>
        ),
      },
      {
        title: customTableTitle(`${t("STATUS")}`, ""),
        dataIndex: "status",
        key: "status",
        width: "15%",
        render: (text: string, record: any) => {
          console.log("button text", text);

          return (
            <div
              className={
                text === "closed"
                  ? "font-black text-gray-500"
                  : "font-black  text-teal-500"
              }
            >
              {text === "closed" ? (
                <CheckCircleFilled />
              ) : (
                <ExclamationCircleFilled />
              )}{" "}
              <span className="pl-2">{t(record.status.toUpperCase())}</span>
            </div>
          );
        },
      },
      {
        title: customTableTitle(`${t("")}`, ""),
        dataIndex: "buttons",
        key: "buttons",
        width: "150px",
        render: (text: string, record: any) => {
          return (
            <div className="flex items-center justify-start">
              <Button
                className="font-black bg-teal-500"
                onClick={() => navigateToChecklist(record)}
                type="primary"
              >
                <ArrowRightOutlined></ArrowRightOutlined>
              </Button>
              <Button
                className="font-black bg-cyan-500 ml-2"
                onClick={() => {
                  setColumns(headerRender(["slotStart", "name", "surgery"]));
                  setSurgeryDetail({ showing: true, ...record });
                }}
                type="primary"
              >
                <EyeFilled></EyeFilled>
              </Button>
            </div>
          );
        },
      },
    ];

    if (filter.length > 0) {
      return cols.filter((col: any) => filter.includes(col.key));
    }

    return cols;
  };

  const [columns, setColumns] = useState(headerRender());

  const [surgeries, setSurgeries] = React.useState([] as DataType[]);
  const [form] = useForm();

  const setDate = async (data?: string) => {
    console.log(currentDate, data);
    if (data !== "today") {
      setCurrentDate([location?.state?.date, location?.state?.date]);
    } else {
      setCurrentDate([
        DateTime.now().startOf("day").toFormat("yyyy-MM-dd"),
        DateTime.now().endOf("day").toFormat("yyyy-MM-dd"),
      ]);
    }
  };

  const searchBarcode = async (value: string) => {
    const patient = await SurgeryService.getPatientByNosological(value);
    if(!patient || !patient.uuid) { 
      message.error(t("NO_PATIENT_FOUND"));
      return;
    }
    const record = await SurgeryService.getSurgeryByNosological(patient.patient_uuid);
    if (record && record.uuid) {
      navigateToChecklist(record as DataType);
      return;
    }

    message.error(t("NO_PATIENT_FOUND"));
  };

  React.useEffect(() => {
    setDate("not_today");
  }, []);

  useEffect(() => {
    if (currentDate && currentDate[0]) {
      setDateToBeShown(currentDate[0]);
      payload.current = [
        {
          field: "t.validityEnd",
          value: "undefined",
          operator: "isValid",
        },
        {
          field: "data.admission_date",
          operator: "isBetween",
          value: currentDate, //["2023-04-06T00:00:00", "2023-04-06T23:59:59"],
          usecase: "date",
        },
        {
          field: "data.surgeryroom_uuid",
          value: CookieService.getSurgeryRoomUUID(),
          operator: "equal",
        },
      ];

      changeShowLoading(
        SurgeryService.getSurgeries(payload.current)
          .then((rawData) => {
            let data: any = _responseFormat(rawData);
            setSurgeries(data);
          })
          .catch((e) => {
            console.log(t("ERROR"));
          })
      );
    }
  }, [currentDate]);

  return (
    <>
      <div className="page-container">
        <div className="xy">
          {dateToBeShown ? (
            <>
              <div className="page-menu-container-between">
                <div className="text-teal-700 text-lg">
                  <CalendarOutlined className="text-teal-700 font-black pr-2" />{" "}
                  <span className="font-black">{dateToBeShown}</span>
                </div>
                <div className="flex justify-between items-center ">
                  <Form
                    name="checklist-form"
                    className="checklist-form"
                    form={form}
                  >
                    <Form.Item name="search" className="search-form-item">
                      <Input
                        addonBefore={
                          <BarcodeOutlined className="text-teal-700" />
                        }
                        addonAfter={
                          <SearchOutlined className="text-teal-700" />
                        }
                        className="mr-4"
                        onKeyUp={(e: any) => {
                          if (e.key === "Enter") {
                            searchBarcode(e.target.value);
                          }
                        }}
                        placeholder={t("SEARCH")}
                        style={{ width: "300px" }}
                        autoFocus={true}
                      ></Input>
                    </Form.Item>
                  </Form>
                  <Button
                    type="primary"
                    className="font-black mr-4"
                    icon={<CalendarFilled />}
                    style={{ backgroundColor: styles.orange }}
                    onClick={() => navigate("/calendar")}
                  >
                    {t("CALENDAR")}
                  </Button>
                  <Button
                    type="primary"
                    icon={<ArrowDownOutlined />}
                    className="font-black"
                    style={{ backgroundColor: styles.lightBlueBgColor }}
                    onClick={() => setDate("today")}
                  >
                    {t("TODAY")}
                  </Button>
                </div>
              </div>
              <div
                className={
                  surgeryDetail.showing
                    ? "grid grid-cols-4 rounded-md"
                    : "block rounded-md"
                }
              >
                <div
                  className={
                    surgeryDetail.showing ? "col-span-2" : " col-span-4"
                  }
                >
                  <div className="sisos-table-container mx-4" style={{
                  }}>
                    <Table
                      size="small"
                      showHeader={true}
                      className="sisos-table sisos-table-nopadding"
                      columns={columns}
                 
                      sticky={true}
                      // rowClassName="surgery-row"
                      pagination={{
                        pageSize: window.innerHeight > 1000 ? 18 : 9,
                        hideOnSinglePage: true,
                        position: ["topCenter"],
                        size: "small",
                      }}
                      dataSource={surgeries}
                      indentSize={0}
                      
                    />
                  </div>
                </div>
                {surgeryDetail.showing && (
                  <div className="ml-4 details-container p-4 rounded-md col-span-2">
                    <div className="flex justify-between items-center  border-b-2 border-gray-500">
                      <div className="flex items-center">
                        <h2 className="text-2xl font-bold text-teal-700">
                          {surgeryDetail.firstName} {surgeryDetail.lastName}
                        </h2>
                      </div>
                      <div className="flex items-center">
                        <div
                          className={
                            surgeryDetail.status === "closed"
                              ? "border border-solid border-grey-200 rounded-md p-2 font-black text-gray-500 mr-2"
                              : "border border-solid border-teal-200 rounded-md p-2 font-black text-teal-500 mr-2"
                          }
                        >
                          {surgeryDetail.status === "closed" ? (
                            <CheckCircleFilled />
                          ) : (
                            <ExclamationCircleFilled />
                          )}
                          <span className="pl-2">
                            {t(surgeryDetail.status.toUpperCase())}
                          </span>
                        </div>
                        <Button
                          type="primary"
                          danger
                          icon={<EyeInvisibleFilled />}
                          className="font-black uppercase"
                          onClick={() => {
                            setColumns(headerRender());
                            setSurgeryDetail(
                              {} as DataType & { showing: boolean }
                            );
                          }}
                        ></Button>
                      </div>
                    </div>
                    <hr className="border-b-1 border-teal-500 bg-transparent opacity-30" />
                    <div className="grid grid-cols-2 gap-4 p-4 rounded-md text-teal-700">
                      <div>
                        <h3 className="text-lg font-semibold flex items-center text-cyan-800 uppercase">
                          <ScissorOutlined className="mr-2" /> Surgery Details
                        </h3>
                        <p>
                          <strong>Description:</strong>{" "}
                          {surgeryDetail.surgery.description}
                        </p>
                        <p>
                          <strong>Code:</strong> {surgeryDetail.surgery.code}
                        </p>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold flex items-center text-cyan-800 uppercase">
                          <ApartmentOutlined className="mr-2" /> Department
                        </h3>
                        <p>{surgeryDetail.wardDept.description}</p>
                      </div>
                    </div>
                    <div className="mt-4  p-4 rounded-md text-teal-700">
                      <h3 className="text-lg font-semibold flex items-center text-cyan-800 uppercase">
                        <DotChartOutlined className="mr-2" /> Additional
                        Information
                      </h3>
                      <p>
                        <strong>Therapies:</strong> {surgeryDetail.therapies}
                      </p>
                      <p>
                        <strong>Anamnesis:</strong> {surgeryDetail.anamnesis}
                      </p>
                      <p>
                        <strong>Note:</strong> {surgeryDetail.note}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </>
  );
}

export default Surgeries;

function _responseFormat(rawData: SurgeryManagment[]): DataType[] {
  return rawData.map((surgeryMgm, index) => {
    return {
      uuid: surgeryMgm.uuid,
      counterBar: surgeryMgm.counterBar,
      key: index,
      patientUuid: surgeryMgm.patient.uuid,
      firstName: surgeryMgm.patient.first_name,
      lastName: surgeryMgm.patient.last_name,
      tin: surgeryMgm.patient.tin,
      birthDay: surgeryMgm.patient.date_of_birth,
      counterValue: surgeryMgm.counterValue,
      revision: surgeryMgm.revision.revisionNumber,
      surgeryBaselistUUID: surgeryMgm.revisionDetail.length
        ? surgeryMgm.revisionDetail[0].surgeryBaselist
        : "",
      operators: surgeryMgm.operators.map((el) => {
        return {
          code: el.info[0]?.code,
          description: el.info[0]?.description,
          first_name: el.info[0]?.first_name,
          last_name: el.info[0]?.last_name,
        };
      }),
      diagnosis: surgeryMgm.diagnosis.map((el) => {
        return {
          code: el.info[0]?.code,
          description: el.info[0]?.description,
        };
      }),
      surgeryDate: surgeryMgm.revisionData[0].admissionDate,
      slotStart: surgeryMgm.revisionDetail[0].start_date,
      therapies: surgeryMgm.revisionData[0].therapies,
      anamnesis: surgeryMgm.revisionData[0].anamnesis,
      contaminated: surgeryMgm.contaminated[0],
      position: surgeryMgm.surgeryPosition[0],
      note: surgeryMgm.revisionData[0].note,
      status: surgeryMgm.revision.currentStatus,
      surgery: {
        code: surgeryMgm.surgery.length ? surgeryMgm.surgery[0].code : "",
        description: surgeryMgm.surgery.length
          ? surgeryMgm.surgery[0].description
          : "",
      },
      wardDept: {
        code: surgeryMgm.wardDepartment.length
          ? surgeryMgm.wardDepartment[0].code
          : "",
        description: surgeryMgm.wardDepartment.length
          ? surgeryMgm.wardDepartment[0].description
          : "",
      },
      wardBed: {
        code: surgeryMgm.wardBed.length ? surgeryMgm.wardBed[0].code : "",
        description: surgeryMgm.wardBed.length
          ? surgeryMgm.wardBed[0].description
          : "",
      },
      destinationBed: {
        code: surgeryMgm.destinationBed.length
          ? surgeryMgm.destinationBed[0].code
          : "",
        description: surgeryMgm.destinationBed.length
          ? surgeryMgm.destinationBed[0].description
          : "",
      },
    };
  });
}
